import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, userID } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    postedBy: '',
    title: '',
    story: '',
    image: '',
    video_url: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'blog'); // Fetch blog data
        if (response.success) {
          setBlogs(Array.isArray(response.data) ? response.data : []);
        } else {
          toast.error(response.message || "Failed to load blog data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch blogs");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      postedBy: userID,
      title: '',
      story: '',
      image: '',
      video_url: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (blogItem) => {
    setSelectedBlog(blogItem);
    setFormData({
      postedBy: blogItem.postedBy || '',
      title: blogItem.title || '',
      story: blogItem.story || '',
      image: blogItem.image_url || '',
      video_url: blogItem.video_url || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (blogItem) => {
    setBlogToDelete(blogItem);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'blog', blogToDelete.id); // Delete blog by ID
      if (response.success) {
        toast.success(`Blog deleted successfully`);
        setBlogs(blogs.filter(item => item.id !== blogToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete blog");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete blog");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setBlogToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, or GIF)");
      return;
    }
    setFormData(prev => ({
      ...prev,
      image: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;

      if (selectedBlog) {
        // Update existing blog
        response = await updateData('proccess.php', 'blog', formData, selectedBlog.id); // Update blog
        if (response.success) {
          toast.success(`Blog updated successfully`);
          setBlogs(blogs.map(item => 
            item.id === selectedBlog.id 
              ? { ...item, ...formData, image: formData.image && typeof formData.image === 'string' ? formData.image : item.image } 
              : item
          ));
        }
      } else {
        // Add new blog
        response = await addData('proccess.php', 'blog', formData); // Add blog
        if (response.success) {
          toast.success(`Blog added successfully`);
          // Assuming response.data is either the new blog or the updated list
          if (Array.isArray(response.data)) {
            setBlogs(response.data);
          } else {
            setBlogs(prev => [...prev, response.data]);
          }
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedBlog(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "postedBy", headerName: "Posted By", flex: 1, minWidth: 120 },
    { field: "title", headerName: "Blog Title", flex: 1, minWidth: 120 },
    { field: "story", headerName: "Story", flex: 1, minWidth: 120 },
    {
      field: "image",
      headerName: "Blog Image",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Blog"}
              style={{ width: 50, height: 50, objectFit: "cover" }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    {
      field: "video_url",
      headerName: "Video URL",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <a
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: colors.blue[400], textDecoration: 'underline' }}
        >
          {params.value || "No video"}
        </a>
      )
    },
    { field: "date", headerName: "Date Posted", flex: 1, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No blogs available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedBlog(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedBlog ? "Edit Blog" : "Add New Blog"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                style={{ width: "100%" }}
                id="title"
                name="title"
                label="Blog Title"
                variant="standard"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="story"
                name="story"
                label="Story"
                variant="standard"
                multiline
                minRows={4}
                maxRows={16}
                value={formData.story}
                onChange={handleFormChange}
                required
              />
              <Box width={"100%"}>
                <label>Blog Image</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  required={!selectedBlog}
                />
              </Box>
              <TextField
                style={{ width: "100%" }}
                id="video_url"
                name="video_url"
                label="Video URL"
                variant="standard"
                value={formData.video_url}
                onChange={handleFormChange}
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedBlog(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedBlog ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{blogToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Blog Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage blogs
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add Blog
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 250px)", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={blogs}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Blog;